import { useEffect, useRef, useCallback } from 'react';
import { useAnimationStore } from '../store/animationStore';

interface PlaybackOptions {
  onFrame?: (time: number, frame: number) => void;
  onLoop?: () => void;
  onComplete?: () => void;
}

export function useAnimationPlayback(options: PlaybackOptions = {}) {
  const {
    playback,
    selectedAnimationId,
    animations,
    setCurrentTime,
    stop,
  } = useAnimationStore();

  const frameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);

  const animation = selectedAnimationId ? animations[selectedAnimationId] : null;

  const tick = useCallback(
    (timestamp: number) => {
      if (!useAnimationStore.getState().playback.isPlaying || !animation) {
        return;
      }

      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      const deltaTime = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;

      const currentPlayback = useAnimationStore.getState().playback;
      const newTime = currentPlayback.currentTime + deltaTime * animation.speed;

      if (newTime >= animation.duration) {
        if (animation.loop) {
          const loopedTime = newTime % animation.duration;
          setCurrentTime(loopedTime);
          options.onLoop?.();
        } else {
          setCurrentTime(animation.duration);
          stop();
          options.onComplete?.();
          return;
        }
      } else {
        setCurrentTime(newTime);
      }

      const frame = Math.round(currentPlayback.currentTime * currentPlayback.fps);
      options.onFrame?.(currentPlayback.currentTime, frame);

      frameRef.current = requestAnimationFrame(tick);
    },
    [animation, setCurrentTime, stop, options]
  );

  useEffect(() => {
    if (playback.isPlaying) {
      lastTimeRef.current = 0;
      frameRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [playback.isPlaying, tick]);

  // Get interpolated values for all tracks at current time
  const getValuesAtCurrentTime = useCallback(() => {
    if (!animation) return {};

    const values: Record<string, Record<string, number | string>> = {};

    animation.tracks.forEach((track) => {
      if (!values[track.targetObjectId]) {
        values[track.targetObjectId] = {};
      }

      const value = useAnimationStore.getState().getValueAtTime(
        animation.id,
        track.id,
        playback.currentTime
      );

      if (value !== undefined) {
        values[track.targetObjectId][track.property] = value;
      }
    });

    return values;
  }, [animation, playback.currentTime]);

  return {
    isPlaying: playback.isPlaying,
    isPaused: playback.isPaused,
    currentTime: playback.currentTime,
    currentFrame: playback.currentFrame,
    fps: playback.fps,
    duration: animation?.duration || 0,
    getValuesAtCurrentTime,
  };
}
